import { FC, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
	Box,
	Button,
	ButtonGroup,
	Card,
	CardContent,
	CardHeader,
} from "@mui/material";
import { Form, Formik } from "formik";

import { Translate } from "@/components/Application/Translate";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import { AlertSave } from "@/helpers/alerts/AlertSave";
import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";

import { NodeEditSection } from "@/model/Procedure/EditNode";
import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";
import { UpdateProcedureNodeRequest } from "@/model/Procedure/UpdateProcedureNodeRequest";

import { UpdateProcedureNode } from "@/service/ProcedureMutations";

export interface NodeInformationProps {
	procedureId: number;
	node: ProcedureDetailNode | undefined;
	editSection: NodeEditSection;
}

const NodeInformation: FC<NodeInformationProps> = (props) => {
	const [editMode, toggleEditMode] = useState<boolean>(false);

	const { procedureId, node } = props;

	const updateProcedureNode = UpdateProcedureNode();

	const handleSubmit = async (values: UpdateProcedureNodeRequest) => {
		try {
			await updateProcedureNode(values);
			toggleEditMode(false);
			AlertRequest.success();
		} catch (error) {
			AlertRequest.failed();
		}
	};

	return (
		<Card style={{ boxShadow: "none" }} className="card card--default">
			<CardHeader
				title={<Translate String="Procedure.Node.Edit.Information"></Translate>}
				titleTypographyProps={{
					fontWeight: "bold",
					marginTop: "none",
				}}
			/>
			<CardContent>
				{editMode ? (
					<Formik
						initialValues={{
							ProcedureId: procedureId,
							Name: node?.Name,
							SchemeId: node?.SchemeId ?? node?.CheckListId,
							Tag: node?.NodeNumber ?? "",
						}}
						onSubmit={handleSubmit}
					>
						<Form>
							<CustomInput
								label="Name"
								translationString="Procedure.Node.Name"
								type="text"
								id="NodeName"
								name="Name"
							/>
							<CustomInput
								label="Node number"
								translationString="Procedure.Node.Number"
								type="text"
								name="Tag"
								id="NodeTagId"
							/>
							<div className="heading-wrapper-right">
								<ButtonGroup
									orientation="vertical"
									aria-label="vertical outlined button group"
									className="card--default--actions"
								>
									<Button
										variant="outlined"
										color="error"
										onClick={() => AlertSave(() => toggleEditMode(false))}
									>
										<CloseIcon />
									</Button>
									<Button type="submit" variant="outlined" color="success">
										<SaveIcon />
									</Button>
								</ButtonGroup>
							</div>
						</Form>
					</Formik>
				) : (
					<Box>
						<div className="row">
							<Translate String="Procedure.Node.Information.Name">
								Name:
							</Translate>
							<p>{node?.Name}</p>
						</div>
						<div className="row">
							<Translate String="Procedure.Node.Information.NodeNumber">
								Node Number:
							</Translate>
							<p>{node?.NodeNumber}</p>
						</div>
						<ButtonGroup className="card--default--actions">
							<Button
								variant="outlined"
								color="info"
								onClick={() => {
									if (editMode) AlertSave(() => toggleEditMode(false));
									else toggleEditMode(!editMode);
								}}
							>
								{editMode ? <CloseIcon /> : <EditIcon />}
							</Button>
						</ButtonGroup>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

export default NodeInformation;
